<script setup lang="ts">
import { computed } from "vue";
import Sheet from "./Sheet.vue";
import SheetContent from "./SheetContent.vue";
import SheetDescription from "./SheetDescription.vue";
import SheetFooter from "./SheetFooter.vue";
import SheetHeader from "./SheetHeader.vue";
import SheetTitle from "./SheetTitle.vue";
import SheetTrigger from "./SheetTrigger.vue";
import { cn } from "@/lib/utils";

interface Props {
  open?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  size?: "small" | "default" | "large" | "full";
  title?: string;
  description?: string;
  showClose?: boolean;
  overlayClass?: string;
  contentClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  side: "right",
  size: "default",
  showClose: true,
  title: undefined,
  description: undefined,
  overlayClass: undefined,
  contentClass: undefined,
});

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const sizeClasses = computed(() => {
  const sideIsVertical = props.side === "left" || props.side === "right";
  const sideIsHorizontal = props.side === "top" || props.side === "bottom";

  if (sideIsVertical) {
    switch (props.size) {
      case "small":
        return "w-3/4 sm:max-w-sm";
      case "large":
        return "w-3/4 sm:max-w-lg";
      case "full":
        return "w-full max-w-full";
      case "default":
      default:
        return "w-3/4 sm:max-w-sm";
    }
  } else if (sideIsHorizontal) {
    switch (props.size) {
      case "small":
        return "h-1/4 max-h-48";
      case "large":
        return "h-3/4 max-h-[75vh]";
      case "full":
        return "h-full max-h-full";
      case "default":
      default:
        return "h-auto";
    }
  }
  return "";
});

const contentClasses = computed(() => {
  const baseClasses = "";
  const customSizeClasses = sizeClasses.value;

  // Remove default size classes and apply custom ones
  return cn(baseClasses, customSizeClasses, props.contentClass);
});
</script>

<template>
  <Sheet :open="open" @update:open="emit('update:open', $event)">
    <SheetTrigger v-if="$slots.trigger" asChild>
      <slot name="trigger" />
    </SheetTrigger>

    <SheetContent :side="side" :class="contentClasses">
      <SheetHeader v-if="title || description || $slots.header">
        <slot name="header">
          <SheetTitle v-if="title">{{ title }}</SheetTitle>
          <SheetDescription v-if="description">{{
            description
          }}</SheetDescription>
        </slot>
      </SheetHeader>

      <div class="flex-1 overflow-y-auto">
        <slot />
      </div>

      <SheetFooter v-if="$slots.footer">
        <slot name="footer" />
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
